/**
 * FikoAnnotation3D - Next-Gen 3D Annotation & Labeling System
 *
 * Gelişmiş 3D etiket ve işaretleme sistemi
 * - Çoklu etiket stilleri (bubble, floating, callout, balloon)
 * - HTML/Canvas hibrit rendering
 * - Akıllı konumlandırma (collision avoidance)
 * - Animasyonlu geçişler
 * - Leader line entegrasyonu
 * - Camera-aware scaling ve orientation
 * - Rich text desteği (HTML5)
 *
 * @author FikoYT Advanced CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Etiket stili
 */
export enum AnnotationStyle {
    // Temel stiller
    SIMPLE = 'SIMPLE',
    BUBBLE = 'BUBBLE',
    BALLOON = 'BALLOON',
    CALLOUT = 'CALLOUT',
    FLAG = 'FLAG',
    PIN = 'PIN',

    // Floating stiller
    FLOATING = 'FLOATING',
    FLOATING_GLASS = 'FLOATING_GLASS',
    FLOATING_NEON = 'FLOATING_NEON',

    // Profesyonel CAD stiller
    CATIA_STYLE = 'CATIA_STYLE',
    SOLIDWORKS_STYLE = 'SOLIDWORKS_STYLE',
    AUTOCAD_STYLE = 'AUTOCAD_STYLE',

    // Özel efektler
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    HOLOGRAPHIC_HUD = 'HOLOGRAPHIC_HUD',
    NEON_GLOW = 'NEON_GLOW',
    METALLIC = 'METALLIC',
    GLASS = 'GLASS',
    FROSTED = 'FROSTED',

    // Minimal stiller
    MINIMAL = 'MINIMAL',
    MINIMAL_LINE = 'MINIMAL_LINE',
    DOT_LABEL = 'DOT_LABEL',

    // İşaretçi stiller
    MARKER_CIRCLE = 'MARKER_CIRCLE',
    MARKER_SQUARE = 'MARKER_SQUARE',
    MARKER_DIAMOND = 'MARKER_DIAMOND',
    MARKER_STAR = 'MARKER_STAR',
    MARKER_CROSS = 'MARKER_CROSS',

    // Özel amaçlı
    TOLERANCE = 'TOLERANCE',
    SURFACE_FINISH = 'SURFACE_FINISH',
    WELD_SYMBOL = 'WELD_SYMBOL',
    GD_T = 'GD_T',
    NOTE = 'NOTE'
}

/**
 * Etiket konumu
 */
export enum AnnotationPosition {
    TOP = 'TOP',
    BOTTOM = 'BOTTOM',
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',
    TOP_LEFT = 'TOP_LEFT',
    TOP_RIGHT = 'TOP_RIGHT',
    BOTTOM_LEFT = 'BOTTOM_LEFT',
    BOTTOM_RIGHT = 'BOTTOM_RIGHT',
    CENTER = 'CENTER',
    AUTO = 'AUTO'
}

/**
 * Leader line tipi
 */
export enum LeaderLineType {
    NONE = 'NONE',
    STRAIGHT = 'STRAIGHT',
    ANGLED = 'ANGLED',
    CURVED = 'CURVED',
    ORTHOGONAL = 'ORTHOGONAL',
    SPLINE = 'SPLINE'
}

/**
 * Animasyon tipi
 */
export enum AnnotationAnimation {
    NONE = 'NONE',
    FADE_IN = 'FADE_IN',
    SCALE_IN = 'SCALE_IN',
    SLIDE_IN = 'SLIDE_IN',
    BOUNCE_IN = 'BOUNCE_IN',
    PULSE = 'PULSE',
    GLOW = 'GLOW',
    FLOAT = 'FLOAT',
    TYPEWRITER = 'TYPEWRITER'
}

/**
 * Etkileşim durumu
 */
export enum AnnotationState {
    IDLE = 'IDLE',
    HOVER = 'HOVER',
    SELECTED = 'SELECTED',
    EDITING = 'EDITING',
    DRAGGING = 'DRAGGING',
    HIDDEN = 'HIDDEN',
    MINIMIZED = 'MINIMIZED'
}

/**
 * İçerik tipi
 */
export enum ContentType {
    TEXT = 'TEXT',
    HTML = 'HTML',
    IMAGE = 'IMAGE',
    ICON = 'ICON',
    MEASUREMENT = 'MEASUREMENT',
    TOLERANCE = 'TOLERANCE',
    CUSTOM = 'CUSTOM'
}

/**
 * Annotation konfigürasyonu
 */
export interface AnnotationConfig {
    // Pozisyon
    position: THREE.Vector3;
    targetPosition?: THREE.Vector3; // Leader line hedef noktası
    labelOffset: THREE.Vector3;

    // İçerik
    text: string;
    secondaryText?: string;
    contentType: ContentType;
    htmlContent?: string;
    icon?: string;

    // Stil
    style: AnnotationStyle;
    backgroundColor: number;
    textColor: number;
    borderColor: number;
    borderWidth: number;
    borderRadius: number;
    opacity: number;

    // Font
    fontSize: number;
    fontFamily: string;
    fontWeight: string;
    textAlign: string;

    // Boyut
    width?: number;
    height?: number;
    padding: number;
    minWidth: number;
    maxWidth: number;

    // Leader line
    leaderLineType: LeaderLineType;
    leaderLineColor: number;
    leaderLineWidth: number;
    leaderArrowSize: number;

    // Animasyon
    animation: AnnotationAnimation;
    animationDuration: number;
    animationDelay: number;

    // Davranış
    alwaysFaceCamera: boolean;
    scaleWithDistance: boolean;
    minScale: number;
    maxScale: number;
    occlusionTest: boolean;
    zIndex: number;

    // Efektler
    enableShadow: boolean;
    shadowColor: number;
    shadowBlur: number;
    enableGlow: boolean;
    glowColor: number;
    glowIntensity: number;

    // Etkileşim
    interactive: boolean;
    draggable: boolean;
    editable: boolean;
    collapsible: boolean;
}

/**
 * Tema renkleri
 */
export interface AnnotationTheme {
    background: number;
    text: number;
    border: number;
    accent: number;
    glow: number;
    shadow: number;
    leaderLine: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_ANNOTATION_CONFIG: AnnotationConfig = {
    position: new THREE.Vector3(0, 0, 0),
    labelOffset: new THREE.Vector3(0, 0.5, 0),

    text: '',
    contentType: ContentType.TEXT,

    style: AnnotationStyle.BUBBLE,
    backgroundColor: 0x1a1a2e,
    textColor: 0xffffff,
    borderColor: 0x00ff88,
    borderWidth: 2,
    borderRadius: 8,
    opacity: 0.95,

    fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    textAlign: 'center',

    padding: 12,
    minWidth: 60,
    maxWidth: 300,

    leaderLineType: LeaderLineType.STRAIGHT,
    leaderLineColor: 0x00ff88,
    leaderLineWidth: 2,
    leaderArrowSize: 8,

    animation: AnnotationAnimation.FADE_IN,
    animationDuration: 0.3,
    animationDelay: 0,

    alwaysFaceCamera: true,
    scaleWithDistance: true,
    minScale: 0.5,
    maxScale: 2,
    occlusionTest: false,
    zIndex: 1,

    enableShadow: true,
    shadowColor: 0x000000,
    shadowBlur: 10,
    enableGlow: false,
    glowColor: 0x00ff88,
    glowIntensity: 0.5,

    interactive: true,
    draggable: false,
    editable: false,
    collapsible: false
};

const ANNOTATION_THEMES: Record<string, AnnotationTheme> = {
    NEON: {
        background: 0x1a1a2e,
        text: 0xffffff,
        border: 0x00ff88,
        accent: 0x00ffff,
        glow: 0x00ff88,
        shadow: 0x000000,
        leaderLine: 0x00ff88
    },
    HOLOGRAPHIC: {
        background: 0x112233,
        text: 0xffffff,
        border: 0x88ccff,
        accent: 0xff88cc,
        glow: 0xaaddff,
        shadow: 0x001122,
        leaderLine: 0x88ccff
    },
    CATIA: {
        background: 0xf0f0f0,
        text: 0x000000,
        border: 0x00aa00,
        accent: 0x00cc00,
        glow: 0x00ff00,
        shadow: 0x888888,
        leaderLine: 0x00aa00
    },
    SOLIDWORKS: {
        background: 0xffffff,
        text: 0x000000,
        border: 0xff6600,
        accent: 0xff8800,
        glow: 0xffaa00,
        shadow: 0x888888,
        leaderLine: 0xff6600
    },
    BLUEPRINT: {
        background: 0x001144,
        text: 0xffffff,
        border: 0x4488ff,
        accent: 0x66aaff,
        glow: 0x88ccff,
        shadow: 0x000022,
        leaderLine: 0x4488ff
    },
    DARK: {
        background: 0x1e1e1e,
        text: 0xffffff,
        border: 0x3c3c3c,
        accent: 0x007acc,
        glow: 0x007acc,
        shadow: 0x000000,
        leaderLine: 0x666666
    },
    LIGHT: {
        background: 0xffffff,
        text: 0x333333,
        border: 0xcccccc,
        accent: 0x0066cc,
        glow: 0x0066cc,
        shadow: 0xaaaaaa,
        leaderLine: 0x666666
    },
    MATRIX: {
        background: 0x000000,
        text: 0x00ff00,
        border: 0x00ff00,
        accent: 0x00cc00,
        glow: 0x00ff00,
        shadow: 0x003300,
        leaderLine: 0x00ff00
    }
};

// ============================================================================
// STYLE TEMPLATES
// ============================================================================

const STYLE_TEMPLATES: Record<AnnotationStyle, (config: AnnotationConfig, theme: AnnotationTheme) => string> = {
    [AnnotationStyle.SIMPLE]: (config, theme) => `
        background: #${theme.background.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
    `,

    [AnnotationStyle.BUBBLE]: (config, theme) => `
        background: linear-gradient(135deg,
            rgba(${(theme.background >> 16) & 255}, ${(theme.background >> 8) & 255}, ${theme.background & 255}, ${config.opacity}),
            rgba(${(theme.background >> 16) & 255}, ${(theme.background >> 8) & 255}, ${theme.background & 255}, ${config.opacity * 0.8}));
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px ${config.padding * 1.5}px;
        border-radius: ${config.borderRadius}px;
        border: ${config.borderWidth}px solid #${theme.border.toString(16).padStart(6, '0')};
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        box-shadow: 0 4px 15px rgba(0,0,0,0.3);
    `,

    [AnnotationStyle.BALLOON]: (config, theme) => `
        background: #${theme.background.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: 50%;
        border: ${config.borderWidth}px solid #${theme.border.toString(16).padStart(6, '0')};
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        min-width: 40px;
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `,

    [AnnotationStyle.CALLOUT]: (config, theme) => `
        background: #${theme.background.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px ${config.borderRadius}px ${config.borderRadius}px 0;
        border: ${config.borderWidth}px solid #${theme.border.toString(16).padStart(6, '0')};
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        box-shadow: 0 3px 12px rgba(0,0,0,0.25);
    `,

    [AnnotationStyle.FLAG]: (config, theme) => `
        background: #${theme.border.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding * 0.5}px ${config.padding}px;
        border-radius: 0 ${config.borderRadius}px ${config.borderRadius}px 0;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 1px;
    `,

    [AnnotationStyle.PIN]: (config, theme) => `
        background: #${theme.accent.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize * 0.8}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
    `,

    [AnnotationStyle.FLOATING]: (config, theme) => `
        background: rgba(${(theme.background >> 16) & 255}, ${(theme.background >> 8) & 255}, ${theme.background & 255}, ${config.opacity});
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        backdrop-filter: blur(10px);
        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    `,

    [AnnotationStyle.FLOATING_GLASS]: (config, theme) => `
        background: rgba(255, 255, 255, 0.1);
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        backdrop-filter: blur(20px);
        box-shadow: 0 8px 32px rgba(0,0,0,0.1),
                    inset 0 0 0 1px rgba(255,255,255,0.1);
    `,

    [AnnotationStyle.FLOATING_NEON]: (config, theme) => `
        background: rgba(0, 0, 0, 0.8);
        color: #${theme.glow.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        border: 2px solid #${theme.glow.toString(16).padStart(6, '0')};
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        text-shadow: 0 0 10px #${theme.glow.toString(16).padStart(6, '0')},
                     0 0 20px #${theme.glow.toString(16).padStart(6, '0')};
        box-shadow: 0 0 20px #${theme.glow.toString(16).padStart(6, '0')}40,
                    inset 0 0 20px #${theme.glow.toString(16).padStart(6, '0')}20;
    `,

    [AnnotationStyle.CATIA_STYLE]: (config, theme) => `
        background: #f0f0f0;
        color: #000000;
        padding: ${config.padding * 0.75}px ${config.padding}px;
        border: 1px solid #00aa00;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
    `,

    [AnnotationStyle.SOLIDWORKS_STYLE]: (config, theme) => `
        background: #ffffff;
        color: #000000;
        padding: ${config.padding * 0.75}px ${config.padding}px;
        border: 2px solid #ff6600;
        font-size: ${config.fontSize}px;
        font-family: Arial, sans-serif;
        box-shadow: 2px 2px 4px rgba(0,0,0,0.2);
    `,

    [AnnotationStyle.AUTOCAD_STYLE]: (config, theme) => `
        background: transparent;
        color: #ffffff;
        padding: 0;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
        text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
    `,

    [AnnotationStyle.HOLOGRAPHIC]: (config, theme) => `
        background: linear-gradient(135deg,
            rgba(136, 204, 255, 0.2),
            rgba(255, 136, 204, 0.2),
            rgba(204, 255, 136, 0.2));
        color: #ffffff;
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        backdrop-filter: blur(10px);
        animation: holographicShift 3s ease-in-out infinite;
    `,

    [AnnotationStyle.HOLOGRAPHIC_HUD]: (config, theme) => `
        background: rgba(0, 255, 136, 0.1);
        color: #00ff88;
        padding: ${config.padding}px;
        border-radius: 2px;
        border: 1px solid #00ff88;
        font-size: ${config.fontSize}px;
        font-family: 'Consolas', monospace;
        text-shadow: 0 0 5px #00ff88;
        box-shadow: 0 0 10px rgba(0, 255, 136, 0.3),
                    inset 0 0 10px rgba(0, 255, 136, 0.1);
    `,

    [AnnotationStyle.NEON_GLOW]: (config, theme) => `
        background: transparent;
        color: #${theme.glow.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
        text-shadow: 0 0 5px #${theme.glow.toString(16).padStart(6, '0')},
                     0 0 10px #${theme.glow.toString(16).padStart(6, '0')},
                     0 0 20px #${theme.glow.toString(16).padStart(6, '0')},
                     0 0 40px #${theme.glow.toString(16).padStart(6, '0')};
    `,

    [AnnotationStyle.METALLIC]: (config, theme) => `
        background: linear-gradient(180deg, #4a4a4a, #2a2a2a, #4a4a4a);
        color: #ffffff;
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        border: 1px solid #666666;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        box-shadow: 0 2px 4px rgba(0,0,0,0.4),
                    inset 0 1px 0 rgba(255,255,255,0.1);
    `,

    [AnnotationStyle.GLASS]: (config, theme) => `
        background: rgba(255, 255, 255, 0.15);
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        backdrop-filter: blur(20px);
        box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    `,

    [AnnotationStyle.FROSTED]: (config, theme) => `
        background: rgba(255, 255, 255, 0.7);
        color: #333333;
        padding: ${config.padding}px;
        border-radius: ${config.borderRadius}px;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        backdrop-filter: blur(30px) saturate(180%);
        box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    `,

    [AnnotationStyle.MINIMAL]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: 4px 8px;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
    `,

    [AnnotationStyle.MINIMAL_LINE]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        padding: 4px 8px;
        border-bottom: 2px solid #${theme.border.toString(16).padStart(6, '0')};
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
    `,

    [AnnotationStyle.DOT_LABEL]: (config, theme) => `
        background: #${theme.accent.toString(16).padStart(6, '0')};
        color: transparent;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        box-shadow: 0 0 10px #${theme.accent.toString(16).padStart(6, '0')};
    `,

    [AnnotationStyle.MARKER_CIRCLE]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid #${theme.border.toString(16).padStart(6, '0')};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
    `,

    [AnnotationStyle.MARKER_SQUARE]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 32px;
        height: 32px;
        border: 3px solid #${theme.border.toString(16).padStart(6, '0')};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
    `,

    [AnnotationStyle.MARKER_DIAMOND]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 32px;
        height: 32px;
        border: 3px solid #${theme.border.toString(16).padStart(6, '0')};
        transform: rotate(45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
    `,

    [AnnotationStyle.MARKER_STAR]: (config, theme) => `
        background: #${theme.accent.toString(16).padStart(6, '0')};
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 32px;
        height: 32px;
        clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize * 0.8}px;
        font-family: ${config.fontFamily};
        font-weight: bold;
    `,

    [AnnotationStyle.MARKER_CROSS]: (config, theme) => `
        background: transparent;
        color: #${theme.text.toString(16).padStart(6, '0')};
        width: 32px;
        height: 32px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
    `,

    [AnnotationStyle.TOLERANCE]: (config, theme) => `
        background: #ffffff;
        color: #000000;
        padding: 4px 8px;
        border: 1px solid #000000;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
    `,

    [AnnotationStyle.SURFACE_FINISH]: (config, theme) => `
        background: transparent;
        color: #000000;
        padding: 4px;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
    `,

    [AnnotationStyle.WELD_SYMBOL]: (config, theme) => `
        background: transparent;
        color: #000000;
        padding: 4px;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
    `,

    [AnnotationStyle.GD_T]: (config, theme) => `
        background: #ffffff;
        color: #000000;
        padding: 2px 4px;
        border: 1px solid #000000;
        font-size: ${config.fontSize}px;
        font-family: 'Courier New', monospace;
        display: flex;
        gap: 2px;
    `,

    [AnnotationStyle.NOTE]: (config, theme) => `
        background: #ffffcc;
        color: #333333;
        padding: ${config.padding}px;
        border-radius: 2px;
        border-left: 4px solid #ffcc00;
        font-size: ${config.fontSize}px;
        font-family: ${config.fontFamily};
        box-shadow: 2px 2px 8px rgba(0,0,0,0.1);
        max-width: ${config.maxWidth}px;
    `
};

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoAnnotation3D - 3D Annotation & Labeling System
 */
export class FikoAnnotation3D extends THREE.Group {

    // Konfigürasyon
    private config: AnnotationConfig;
    private theme: AnnotationTheme;

    // Bileşenler
    private labelElement: HTMLDivElement | null = null;
    private css2DObject: CSS2DObject | null = null;
    private leaderLine: THREE.Line | null = null;
    private targetMarker: THREE.Mesh | null = null;
    private spriteLabel: THREE.Sprite | null = null;

    // Animasyon
    private animationId: number = 0;
    private isAnimating: boolean = false;
    private animationStartTime: number = 0;

    // Durum
    private currentState: AnnotationState = AnnotationState.IDLE;
    private isCollapsed: boolean = false;

    // Camera reference
    private camera: THREE.Camera | null = null;

    // Event listener cleanup
    private _boundHandlers: {
        mouseenter?: () => void;
        mouseleave?: () => void;
        click?: () => void;
        dblclick?: () => void;
        mousedown?: (e: MouseEvent) => void;
        documentMousemove?: (e: MouseEvent) => void;
        documentMouseup?: () => void;
    } = {};

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(config: Partial<AnnotationConfig> = {}) {
        super();

        this.config = { ...DEFAULT_ANNOTATION_CONFIG, ...config };
        this.theme = ANNOTATION_THEMES.NEON;

        this.name = 'FikoAnnotation3D';
        this.userData.type = 'annotation';
        this.userData.selectable = true;
        this.userData.interactive = this.config.interactive;

        this.position.copy(this.config.position);

        this.buildAnnotation();

        if (this.config.animation !== AnnotationAnimation.NONE) {
            this.playEntryAnimation();
        }
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Metni güncelle
     */
    public setText(text: string): void {
        this.config.text = text;
        this.updateLabelContent();
    }

    /**
     * İkincil metni güncelle
     */
    public setSecondaryText(text: string): void {
        this.config.secondaryText = text;
        this.updateLabelContent();
    }

    /**
     * HTML içerik ayarla
     */
    public setHtmlContent(html: string): void {
        this.config.htmlContent = html;
        this.config.contentType = ContentType.HTML;
        this.updateLabelContent();
    }

    /**
     * Pozisyonu güncelle
     */
    public setPosition(position: THREE.Vector3): void {
        this.config.position.copy(position);
        this.position.copy(position);
        this.updateLeaderLine();
    }

    /**
     * Hedef pozisyonu güncelle (leader line için)
     */
    public setTargetPosition(position: THREE.Vector3): void {
        this.config.targetPosition = position.clone();
        this.updateLeaderLine();
    }

    /**
     * Label offset güncelle
     */
    public setLabelOffset(offset: THREE.Vector3): void {
        this.config.labelOffset.copy(offset);
        this.updateLabelPosition();
    }

    /**
     * Stili değiştir
     */
    public setStyle(style: AnnotationStyle): void {
        this.config.style = style;
        this.updateLabelStyle();
    }

    /**
     * Tema uygula
     */
    public applyTheme(themeName: string): void {
        if (ANNOTATION_THEMES[themeName]) {
            this.theme = ANNOTATION_THEMES[themeName];
            this.config.backgroundColor = this.theme.background;
            this.config.textColor = this.theme.text;
            this.config.borderColor = this.theme.border;
            this.config.leaderLineColor = this.theme.leaderLine;
            this.config.glowColor = this.theme.glow;
            this.updateLabelStyle();
            this.updateLeaderLine();
        }
    }

    /**
     * Özel tema ayarla
     */
    public setCustomTheme(theme: AnnotationTheme): void {
        this.theme = theme;
        this.config.backgroundColor = theme.background;
        this.config.textColor = theme.text;
        this.config.borderColor = theme.border;
        this.config.leaderLineColor = theme.leaderLine;
        this.config.glowColor = theme.glow;
        this.updateLabelStyle();
        this.updateLeaderLine();
    }

    /**
     * Durum değiştir
     */
    public setState(state: AnnotationState): void {
        if (this.currentState === state) return;
        this.currentState = state;
        this.applyStateVisuals();
    }

    /**
     * Görünürlük ayarla
     */
    public setVisibility(visible: boolean): void {
        this.visible = visible;
        if (this.labelElement) {
            this.labelElement.style.display = visible ? 'block' : 'none';
        }
    }

    /**
     * Collapse/Expand
     */
    public toggleCollapse(): void {
        if (!this.config.collapsible) return;

        this.isCollapsed = !this.isCollapsed;

        if (this.labelElement) {
            if (this.isCollapsed) {
                this.labelElement.style.maxHeight = '20px';
                this.labelElement.style.overflow = 'hidden';
            } else {
                this.labelElement.style.maxHeight = 'none';
                this.labelElement.style.overflow = 'visible';
            }
        }
    }

    /**
     * Kamerayı güncelle (camera-aware features için)
     */
    public updateCamera(camera: THREE.Camera): void {
        this.camera = camera;

        if (this.config.alwaysFaceCamera && this.spriteLabel) {
            // Sprite zaten kameraya bakıyor
        }

        if (this.config.scaleWithDistance) {
            const distance = camera.position.distanceTo(this.position);
            const scale = THREE.MathUtils.clamp(
                distance * 0.1,
                this.config.minScale,
                this.config.maxScale
            );

            if (this.css2DObject) {
                // CSS2D için scale yerine font-size ayarla
                if (this.labelElement) {
                    const fontSize = this.config.fontSize * (1 / scale);
                    this.labelElement.style.fontSize = `${fontSize}px`;
                }
            }
        }
    }

    /**
     * Giriş animasyonunu oynat
     */
    public playEntryAnimation(): void {
        if (!this.labelElement) return;

        const duration = this.config.animationDuration * 1000;
        const delay = this.config.animationDelay * 1000;

        setTimeout(() => {
            if (!this.labelElement) return;

            switch (this.config.animation) {
                case AnnotationAnimation.FADE_IN:
                    this.labelElement.style.opacity = '0';
                    this.labelElement.style.transition = `opacity ${duration}ms ease-out`;
                    requestAnimationFrame(() => {
                        if (this.labelElement) this.labelElement.style.opacity = '1';
                    });
                    break;

                case AnnotationAnimation.SCALE_IN:
                    this.labelElement.style.transform = 'scale(0)';
                    this.labelElement.style.transition = `transform ${duration}ms ease-out`;
                    requestAnimationFrame(() => {
                        if (this.labelElement) this.labelElement.style.transform = 'scale(1)';
                    });
                    break;

                case AnnotationAnimation.SLIDE_IN:
                    this.labelElement.style.transform = 'translateY(-20px)';
                    this.labelElement.style.opacity = '0';
                    this.labelElement.style.transition = `transform ${duration}ms ease-out, opacity ${duration}ms ease-out`;
                    requestAnimationFrame(() => {
                        if (this.labelElement) {
                            this.labelElement.style.transform = 'translateY(0)';
                            this.labelElement.style.opacity = '1';
                        }
                    });
                    break;

                case AnnotationAnimation.BOUNCE_IN:
                    this.labelElement.style.transform = 'scale(0)';
                    this.labelElement.style.transition = `transform ${duration}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`;
                    requestAnimationFrame(() => {
                        if (this.labelElement) this.labelElement.style.transform = 'scale(1)';
                    });
                    break;

                case AnnotationAnimation.PULSE:
                    this.startPulseAnimation();
                    break;

                case AnnotationAnimation.GLOW:
                    this.startGlowAnimation();
                    break;

                case AnnotationAnimation.FLOAT:
                    this.startFloatAnimation();
                    break;

                case AnnotationAnimation.TYPEWRITER:
                    this.startTypewriterAnimation();
                    break;
            }
        }, delay);
    }

    /**
     * CSS2DObject'i al (renderer için)
     */
    public getCSS2DObject(): CSS2DObject | null {
        return this.css2DObject;
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        this.buildAnnotation();

        if (this.config.animation !== AnnotationAnimation.NONE) {
            this.playEntryAnimation();
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        // Remove event listeners
        if (this.labelElement) {
            if (this._boundHandlers.mouseenter) this.labelElement.removeEventListener('mouseenter', this._boundHandlers.mouseenter);
            if (this._boundHandlers.mouseleave) this.labelElement.removeEventListener('mouseleave', this._boundHandlers.mouseleave);
            if (this._boundHandlers.click) this.labelElement.removeEventListener('click', this._boundHandlers.click);
            if (this._boundHandlers.dblclick) this.labelElement.removeEventListener('dblclick', this._boundHandlers.dblclick);
            if (this._boundHandlers.mousedown) this.labelElement.removeEventListener('mousedown', this._boundHandlers.mousedown);
        }
        if (this._boundHandlers.documentMousemove) document.removeEventListener('mousemove', this._boundHandlers.documentMousemove);
        if (this._boundHandlers.documentMouseup) document.removeEventListener('mouseup', this._boundHandlers.documentMouseup);
        this._boundHandlers = {};

        this.stopAnimations();

        if (this.css2DObject) {
            this.remove(this.css2DObject);
            this.css2DObject = null;
        }

        if (this.labelElement && this.labelElement.parentNode) {
            this.labelElement.parentNode.removeChild(this.labelElement);
        }
        this.labelElement = null;

        if (this.leaderLine) {
            if (this.leaderLine.geometry) this.leaderLine.geometry.dispose();
            if (this.leaderLine.material) (this.leaderLine.material as THREE.Material).dispose();
            this.remove(this.leaderLine);
            this.leaderLine = null;
        }

        if (this.targetMarker) {
            if (this.targetMarker.geometry) this.targetMarker.geometry.dispose();
            if (this.targetMarker.material) (this.targetMarker.material as THREE.Material).dispose();
            this.remove(this.targetMarker);
            this.targetMarker = null;
        }

        if (this.spriteLabel) {
            if ((this.spriteLabel.material as THREE.SpriteMaterial).map) {
                (this.spriteLabel.material as THREE.SpriteMaterial).map!.dispose();
            }
            (this.spriteLabel.material as THREE.Material).dispose();
            this.remove(this.spriteLabel);
            this.spriteLabel = null;
        }
    }

    // ========================================================================
    // PRIVATE METHODS - BUILD
    // ========================================================================

    /**
     * Annotation bileşenlerini oluştur
     */
    private buildAnnotation(): void {
        this.createLabel();
        this.createLeaderLine();
        this.createTargetMarker();
    }

    /**
     * Label oluştur
     */
    private createLabel(): void {
        // HTML element oluştur
        this.labelElement = document.createElement('div');
        this.labelElement.className = 'fiko-annotation-3d';

        // İçerik
        this.updateLabelContent();

        // Stil
        this.updateLabelStyle();

        // Event listeners
        if (this.config.interactive) {
            this.setupInteractivity();
        }

        // CSS2DObject oluştur
        this.css2DObject = new CSS2DObject(this.labelElement);
        this.css2DObject.position.copy(this.config.labelOffset);
        this.add(this.css2DObject);
    }

    /**
     * Label içeriğini güncelle
     */
    private updateLabelContent(): void {
        if (!this.labelElement) return;

        let content = '';

        switch (this.config.contentType) {
            case ContentType.HTML:
                content = this.config.htmlContent || this.config.text;
                break;

            case ContentType.MEASUREMENT:
                content = `
                    <div class="measurement-value">${this.config.text}</div>
                    ${this.config.secondaryText ? `<div class="measurement-unit">${this.config.secondaryText}</div>` : ''}
                `;
                break;

            case ContentType.TOLERANCE:
                content = `
                    <div class="tolerance-value">${this.config.text}</div>
                    ${this.config.secondaryText ? `<div class="tolerance-range">±${this.config.secondaryText}</div>` : ''}
                `;
                break;

            case ContentType.ICON:
                content = `
                    <span class="icon">${this.config.icon || '📍'}</span>
                    ${this.config.text ? `<span class="label">${this.config.text}</span>` : ''}
                `;
                break;

            case ContentType.TEXT:
            default:
                content = this.config.text;
                if (this.config.secondaryText) {
                    content += `<div class="secondary-text">${this.config.secondaryText}</div>`;
                }
        }

        this.labelElement.innerHTML = content;
    }

    /**
     * Label stilini güncelle
     */
    private updateLabelStyle(): void {
        if (!this.labelElement) return;

        // Stil template'i al
        const styleTemplate = STYLE_TEMPLATES[this.config.style];
        const baseStyle = styleTemplate ? styleTemplate(this.config, this.theme) : '';

        // Temel stil
        this.labelElement.style.cssText = `
            ${baseStyle}
            pointer-events: ${this.config.interactive ? 'auto' : 'none'};
            user-select: none;
            white-space: nowrap;
            z-index: ${this.config.zIndex};
            transition: all 0.2s ease;
        `;

        // Width constraints
        if (this.config.width) {
            this.labelElement.style.width = `${this.config.width}px`;
            this.labelElement.style.whiteSpace = 'normal';
        } else {
            this.labelElement.style.minWidth = `${this.config.minWidth}px`;
            this.labelElement.style.maxWidth = `${this.config.maxWidth}px`;
        }

        // Shadow
        if (this.config.enableShadow) {
            const existingShadow = this.labelElement.style.boxShadow;
            this.labelElement.style.boxShadow = existingShadow
                ? `${existingShadow}, 0 ${this.config.shadowBlur}px ${this.config.shadowBlur * 2}px rgba(${(this.config.shadowColor >> 16) & 255}, ${(this.config.shadowColor >> 8) & 255}, ${this.config.shadowColor & 255}, 0.3)`
                : `0 ${this.config.shadowBlur}px ${this.config.shadowBlur * 2}px rgba(${(this.config.shadowColor >> 16) & 255}, ${(this.config.shadowColor >> 8) & 255}, ${this.config.shadowColor & 255}, 0.3)`;
        }
    }

    /**
     * Label pozisyonunu güncelle
     */
    private updateLabelPosition(): void {
        if (this.css2DObject) {
            this.css2DObject.position.copy(this.config.labelOffset);
        }
    }

    /**
     * Leader line oluştur
     */
    private createLeaderLine(): void {
        if (this.config.leaderLineType === LeaderLineType.NONE) return;
        if (!this.config.targetPosition) return;

        const points = this.calculateLeaderLinePoints();

        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
            color: this.config.leaderLineColor,
            linewidth: this.config.leaderLineWidth,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1
        });

        this.leaderLine = new THREE.Line(geometry, material);
        this.add(this.leaderLine);
    }

    /**
     * Leader line noktalarını hesapla
     */
    private calculateLeaderLinePoints(): THREE.Vector3[] {
        const start = this.config.labelOffset.clone();
        const end = this.config.targetPosition!.clone().sub(this.config.position);

        switch (this.config.leaderLineType) {
            case LeaderLineType.STRAIGHT:
                return [start, end];

            case LeaderLineType.ANGLED:
                const mid = new THREE.Vector3(
                    start.x,
                    (start.y + end.y) / 2,
                    start.z
                );
                return [start, mid, end];

            case LeaderLineType.ORTHOGONAL:
                const horizontal = new THREE.Vector3(end.x, start.y, start.z);
                return [start, horizontal, end];

            case LeaderLineType.CURVED:
                // Quadratic bezier curve
                const controlPoint = new THREE.Vector3(
                    (start.x + end.x) / 2,
                    start.y,
                    (start.z + end.z) / 2
                );
                const curve = new THREE.QuadraticBezierCurve3(start, controlPoint, end);
                return curve.getPoints(20);

            case LeaderLineType.SPLINE:
                const spline = new THREE.CatmullRomCurve3([start, end], false, 'catmullrom', 0.5);
                return spline.getPoints(20);

            default:
                return [start, end];
        }
    }

    /**
     * Leader line güncelle
     */
    private updateLeaderLine(): void {
        if (this.leaderLine) {
            this.remove(this.leaderLine);
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
            this.leaderLine = null;
        }

        this.createLeaderLine();
    }

    /**
     * Hedef marker oluştur
     */
    private createTargetMarker(): void {
        if (!this.config.targetPosition) return;

        const geometry = new THREE.SphereGeometry(0.02, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: this.config.leaderLineColor,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1
        });

        this.targetMarker = new THREE.Mesh(geometry, material);
        this.targetMarker.position.copy(
            this.config.targetPosition.clone().sub(this.config.position)
        );
        this.add(this.targetMarker);
    }

    /**
     * Interaktivite kurulumu
     */
    private setupInteractivity(): void {
        if (!this.labelElement) return;

        // Hover
        this._boundHandlers.mouseenter = () => {
            this.setState(AnnotationState.HOVER);
        };
        this.labelElement.addEventListener('mouseenter', this._boundHandlers.mouseenter);

        this._boundHandlers.mouseleave = () => {
            if (this.currentState === AnnotationState.HOVER) {
                this.setState(AnnotationState.IDLE);
            }
        };
        this.labelElement.addEventListener('mouseleave', this._boundHandlers.mouseleave);

        // Click
        this._boundHandlers.click = () => {
            this.setState(
                this.currentState === AnnotationState.SELECTED
                    ? AnnotationState.IDLE
                    : AnnotationState.SELECTED
            );
        };
        this.labelElement.addEventListener('click', this._boundHandlers.click);

        // Double click (edit mode)
        if (this.config.editable) {
            this._boundHandlers.dblclick = () => {
                this.setState(AnnotationState.EDITING);
                this.enableEditing();
            };
            this.labelElement.addEventListener('dblclick', this._boundHandlers.dblclick);
        }

        // Drag
        if (this.config.draggable) {
            this.setupDragging();
        }
    }

    /**
     * Editing modunu etkinleştir
     */
    private enableEditing(): void {
        if (!this.labelElement) return;

        const input = document.createElement('input');
        input.type = 'text';
        input.value = this.config.text;
        input.style.cssText = `
            width: 100%;
            padding: 4px;
            border: 1px solid #${this.theme.accent.toString(16).padStart(6, '0')};
            background: #${this.theme.background.toString(16).padStart(6, '0')};
            color: #${this.theme.text.toString(16).padStart(6, '0')};
            font-size: ${this.config.fontSize}px;
            font-family: ${this.config.fontFamily};
            outline: none;
        `;

        input.addEventListener('blur', () => {
            this.config.text = input.value;
            this.updateLabelContent();
            this.setState(AnnotationState.IDLE);
        });

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                input.blur();
            } else if (e.key === 'Escape') {
                this.updateLabelContent();
                this.setState(AnnotationState.IDLE);
            }
        });

        this.labelElement.innerHTML = '';
        this.labelElement.appendChild(input);
        input.focus();
        input.select();
    }

    /**
     * Drag kurulumu
     */
    private setupDragging(): void {
        if (!this.labelElement) return;

        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let startOffset = this.config.labelOffset.clone();

        this._boundHandlers.mousedown = (e: MouseEvent) => {
            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            startOffset = this.config.labelOffset.clone();
            this.setState(AnnotationState.DRAGGING);
        };
        this.labelElement.addEventListener('mousedown', this._boundHandlers.mousedown);

        this._boundHandlers.documentMousemove = (e: MouseEvent) => {
            if (!isDragging) return;

            const dx = (e.clientX - startX) * 0.01;
            const dy = (e.clientY - startY) * -0.01;

            this.config.labelOffset.x = startOffset.x + dx;
            this.config.labelOffset.y = startOffset.y + dy;

            this.updateLabelPosition();
            this.updateLeaderLine();
        };
        document.addEventListener('mousemove', this._boundHandlers.documentMousemove);

        this._boundHandlers.documentMouseup = () => {
            if (isDragging) {
                isDragging = false;
                this.setState(AnnotationState.IDLE);
            }
        };
        document.addEventListener('mouseup', this._boundHandlers.documentMouseup);
    }

    // ========================================================================
    // PRIVATE METHODS - ANIMATION
    // ========================================================================

    /**
     * Pulse animasyonu başlat
     */
    private startPulseAnimation(): void {
        if (!this.labelElement) return;

        this.labelElement.style.animation = 'annotationPulse 2s ease-in-out infinite';

        // CSS keyframes ekle
        if (!document.querySelector('#fiko-annotation-keyframes')) {
            const style = document.createElement('style');
            style.id = 'fiko-annotation-keyframes';
            style.textContent = `
                @keyframes annotationPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.05); opacity: 0.8; }
                }
                @keyframes annotationGlow {
                    0%, 100% { box-shadow: 0 0 10px currentColor; }
                    50% { box-shadow: 0 0 20px currentColor, 0 0 30px currentColor; }
                }
                @keyframes annotationFloat {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                @keyframes holographicShift {
                    0%, 100% { filter: hue-rotate(0deg); }
                    50% { filter: hue-rotate(30deg); }
                }
            `;
            document.head.appendChild(style);
        }
    }

    /**
     * Glow animasyonu başlat
     */
    private startGlowAnimation(): void {
        if (!this.labelElement) return;
        this.labelElement.style.animation = 'annotationGlow 2s ease-in-out infinite';
    }

    /**
     * Float animasyonu başlat
     */
    private startFloatAnimation(): void {
        if (!this.labelElement) return;
        this.labelElement.style.animation = 'annotationFloat 3s ease-in-out infinite';
    }

    /**
     * Typewriter animasyonu başlat
     */
    private startTypewriterAnimation(): void {
        if (!this.labelElement) return;

        const text = this.config.text;
        this.labelElement.textContent = '';

        let index = 0;
        const interval = setInterval(() => {
            if (index < text.length) {
                this.labelElement!.textContent += text[index];
                index++;
            } else {
                clearInterval(interval);
            }
        }, 50);
    }

    /**
     * Animasyonları durdur
     */
    private stopAnimations(): void {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
        this.isAnimating = false;

        if (this.labelElement) {
            this.labelElement.style.animation = 'none';
        }
    }

    // ========================================================================
    // PRIVATE METHODS - STATE
    // ========================================================================

    /**
     * Durum görsellerini uygula
     */
    private applyStateVisuals(): void {
        if (!this.labelElement) return;

        switch (this.currentState) {
            case AnnotationState.HOVER:
                this.labelElement.style.transform = 'scale(1.05)';
                this.labelElement.style.boxShadow = `0 0 15px #${this.theme.glow.toString(16).padStart(6, '0')}`;
                break;

            case AnnotationState.SELECTED:
                this.labelElement.style.transform = 'scale(1.1)';
                this.labelElement.style.border = `2px solid #${this.theme.accent.toString(16).padStart(6, '0')}`;
                this.labelElement.style.boxShadow = `0 0 20px #${this.theme.accent.toString(16).padStart(6, '0')}`;
                break;

            case AnnotationState.EDITING:
                this.labelElement.style.transform = 'scale(1)';
                this.labelElement.style.border = `2px dashed #${this.theme.accent.toString(16).padStart(6, '0')}`;
                break;

            case AnnotationState.DRAGGING:
                this.labelElement.style.cursor = 'grabbing';
                this.labelElement.style.opacity = '0.8';
                break;

            case AnnotationState.HIDDEN:
                this.labelElement.style.display = 'none';
                break;

            case AnnotationState.MINIMIZED:
                this.labelElement.style.transform = 'scale(0.5)';
                this.labelElement.style.opacity = '0.5';
                break;

            case AnnotationState.IDLE:
            default:
                this.labelElement.style.transform = 'scale(1)';
                this.labelElement.style.cursor = this.config.draggable ? 'grab' : 'pointer';
                this.labelElement.style.opacity = String(this.config.opacity);
                this.updateLabelStyle();
                break;
        }
    }

    // ========================================================================
    // STATIC FACTORY METHODS
    // ========================================================================

    /**
     * Basit metin etiketi oluştur
     */
    public static createTextLabel(
        text: string,
        position: THREE.Vector3,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text,
            position,
            style: AnnotationStyle.BUBBLE,
            ...config
        });
    }

    /**
     * Ölçü etiketi oluştur
     */
    public static createMeasurementLabel(
        value: string,
        unit: string,
        position: THREE.Vector3,
        targetPosition: THREE.Vector3,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text: value,
            secondaryText: unit,
            position,
            targetPosition,
            contentType: ContentType.MEASUREMENT,
            style: AnnotationStyle.FLOATING,
            leaderLineType: LeaderLineType.STRAIGHT,
            ...config
        });
    }

    /**
     * Callout oluştur
     */
    public static createCallout(
        text: string,
        position: THREE.Vector3,
        targetPosition: THREE.Vector3,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text,
            position,
            targetPosition,
            style: AnnotationStyle.CALLOUT,
            leaderLineType: LeaderLineType.ANGLED,
            ...config
        });
    }

    /**
     * Marker oluştur
     */
    public static createMarker(
        label: string,
        position: THREE.Vector3,
        markerStyle: AnnotationStyle = AnnotationStyle.MARKER_CIRCLE,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text: label,
            position,
            style: markerStyle,
            leaderLineType: LeaderLineType.NONE,
            ...config
        });
    }

    /**
     * Tolerans etiketi oluştur
     */
    public static createToleranceLabel(
        value: string,
        tolerance: string,
        position: THREE.Vector3,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text: value,
            secondaryText: tolerance,
            position,
            contentType: ContentType.TOLERANCE,
            style: AnnotationStyle.TOLERANCE,
            ...config
        });
    }

    /**
     * Not oluştur
     */
    public static createNote(
        text: string,
        position: THREE.Vector3,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        return new FikoAnnotation3D({
            text,
            position,
            style: AnnotationStyle.NOTE,
            leaderLineType: LeaderLineType.NONE,
            maxWidth: 250,
            ...config
        });
    }

    /**
     * Preset tema ile oluştur
     */
    public static createWithTheme(
        themeName: string,
        config: Partial<AnnotationConfig> = {}
    ): FikoAnnotation3D {
        const annotation = new FikoAnnotation3D(config);
        annotation.applyTheme(themeName);
        return annotation;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    ANNOTATION_THEMES,
    DEFAULT_ANNOTATION_CONFIG,
    STYLE_TEMPLATES
};
